/**
 * Screen tokens for the guest signing view (`/document/sign/do?...&signer=true`)
 * — the "Signing View" frame of `Signer Experience.dc.html`.
 *
 * Every value below is read straight off the design; components in this screen
 * consume these instead of literals. Kept local to the screen on purpose (see
 * UI_IMPLEMENTATION_RULES.md § Temporary Screen Tokens).
 *
 * The palette below no longer diverges from `theme.palette.signer.*` — the
 * globalised set carried de-tinted greys (white canvas, neutral-grey rail) and
 * has since been restored to the design's lavenders, so the two agree. Fold
 * this file into the theme once the remaining signer screens ship.
 */

import { formField, inkfree, signer, white } from "theme/colors";

const palette = {
  ink: signer.ink,
  body: signer.text.secondary,
  meta: signer.text.meta,
  muted: signer.text.faint,
  disabled: signer.border.strong,

  white,
  canvas: signer.surface.canvas,
  hover: signer.surface.subtle,
  railRest: signer.surface.muted,

  borderLight: signer.border.light,
  border: signer.border.main,
  borderStrong: signer.border.strong,

  accent: signer.orange.main,
  accentDark: signer.orange.dark,
  /**
   * Solid CTA fills (Start/Finish/Sign buttons, active nav-rail item,
   * fallback avatar colour) — per the standing brand-colour rule these are
   * the app brand red, not `accent`/`accentDark` above, which stay orange
   * for borders/text/icon strokes.
   */
  ctaBg: inkfree.main,
  ctaBgHover: inkfree.dark,
  accentBorder: signer.orange.border,
  accentTint: signer.orange.tint,
  accentFaintest: signer.orange.faintest,
  progressTrack: signer.orange.progressTrack,

  danger: signer.danger.main,
  dangerBorder: signer.danger.borderStrong,

  /** Signature stroke / adopted-signature ink. */
  signatureInk: signer.signatureInk,
  placeholder: formField.placeholderMuted,

  purple: signer.purple.main,
  purpleBorder: signer.purple.border,
  purpleTint: signer.purple.tint,
  success: signer.success.main,
  successBorder: signer.success.border,
  successTint: signer.success.tint,

  /** Floating field navigator dot — filled vs. still empty. */
  dotDone: "#4ADE80",
  dotTodo: signer.gradient.amber,

  /** Finish button while required fields remain. */
  inactive: signer.disabled,
};

const signerExperienceTokens = {
  palette,

  /** Top bar — logo mark, greeting, status chip, and the action cluster. */
  header: {
    height: 64,
    paddingX: "20px",
    gap: "16px",
    background: palette.white,
    borderBottom: `1px solid ${palette.borderLight}`,

    markWidth: 26,
    identityGap: "12px",
    /** Between the fields chip and the "Start signing" button. */
    groupGap: "12px",
    greetingGap: "1px",
    greetingFontSize: "14px",
    greetingFontWeight: 800,
    greetingColor: palette.ink,
    subtitleFontSize: "11.5px",
    subtitleColor: palette.meta,
    /** The design's spans sit on `line-height: normal`, not MUI's 1.5. */
    greetingLineHeight: "normal",

    /** Fields-remaining chip. */
    chipHeight: 36,
    chipPaddingX: "14px",
    chipGap: "8px",
    chipRadius: "999px",
    chipDotSize: 8,
    chipFontSize: "12.5px",
    chipFontWeight: 700,

    /** "Start signing" / "Signed". */
    startHeight: 40,
    startPaddingX: "20px",
    startRadius: "9px",
    startGap: "8px",
    startIconSize: 15,
    startFontSize: "13.5px",
    startFontWeight: 700,

    dividerWidth: "1px",
    dividerHeight: 28,
    dividerColor: palette.borderLight,

    actionsGap: "10px",
    actionHeight: 40,
    actionPaddingX: "14px",
    actionRadius: "9px",
    actionBorderWidth: "1.5px",
    actionFontSize: "13px",
    actionFontWeight: 700,

    finishPaddingX: "26px",
    finishFontSize: "14px",
  },

  /**
   * Document canvas. Laid out like the decline page: the canvas column and the
   * toolbar are fixed, and only the page sheet scrolls. That keeps the toolbar
   * permanently in view without a sticky element the PDF can bleed over.
   */
  canvas: {
    background: palette.canvas,
    paddingTop: "28px",
    paddingX: "36px",
    paddingBottom: "20px",
    contentMaxWidth: 820,
    /**
     * Breathing room inside the sheet's scroll area so the last page clears the
     * floating field navigator. Bottom only — padding on the sheet's other
     * edges would move the field overlays' origin.
     */
    sheetScrollPaddingBottom: "72px",

    /** Document / page toolbar above the page card. */
    toolbarGap: "8px",
    toolbarMarginBottom: "14px",
    navButtonSize: 34,
    navButtonRadius: "8px",
    navIconSize: 11,

    docButtonHeight: 34,
    docButtonMaxWidth: 360,
    docButtonPaddingX: "12px",
    docButtonRadius: "8px",
    docButtonBorderWidth: "1.5px",
    docButtonGap: "9px",
    docButtonFontSize: "12.5px",
    docButtonFontWeight: 700,
    docPosFontSize: "11px",

    pageButtonSize: 30,
    pageButtonRadius: "8px",
    pageNavGap: "6px",
    pageLabelFontSize: "12.5px",
    pageLabelFontWeight: 700,
    pageLabelPaddingX: "6px",

    /** The white page sheet the PDF renders into. */
    pageRadius: "12px",
    pageBorder: `1px solid ${palette.border}`,
    pageShadow: "0 6px 24px rgba(33, 21, 71, 0.06)",

    /**
     * "Review only" pill. The design draws it inside the page card; a real PDF
     * page is full-bleed, so it sits just above the sheet instead — same
     * position to the eye, and it can't shift the field overlays' origin.
     */
    reviewPillHeight: 26,
    reviewPillPaddingX: "12px",
    reviewPillGap: "8px",
    reviewPillRadius: "999px",
    reviewPillBg: palette.purpleTint,
    reviewPillBorder: `1px solid ${palette.purpleBorder}`,
    reviewPillIconSize: 12,
    reviewPillFontSize: "11px",
    reviewPillFontWeight: 700,
    reviewPillMarginBottom: "12px",
  },

  /** Document picker menu opened from the toolbar's file button. */
  docMenu: {
    top: 40,
    width: 340,
    maxHeight: 320,
    radius: "12px",
    padding: "6px",
    shadow: "0 12px 36px rgba(33, 21, 71, 0.14)",
    itemGap: "10px",
    itemPadding: "9px 12px",
    itemRadius: "8px",
    numWidth: 20,
    numFontSize: "11px",
    nameFontSize: "12.5px",
    nameFontWeight: 600,
    badgeFontSize: "10px",
    badgeRadius: "999px",
    badgePadding: "2px 8px",
    checkSize: 12,
  },

  /** Floating field navigator pinned to the bottom of the canvas. */
  fieldNav: {
    bottom: 22,
    background: palette.ink,
    radius: "999px",
    padding: "5px 6px",
    gap: "4px",
    shadow: "0 10px 30px rgba(33, 21, 71, 0.35)",

    arrowSize: 34,
    arrowBackground: "rgba(255, 255, 255, 0.08)",
    arrowHoverBackground: "rgba(255, 255, 255, 0.18)",
    arrowIconSize: 13,

    labelPaddingX: "12px",
    labelGap: "8px",
    labelMinWidth: 150,
    dotSize: 7,
    labelFontSize: "12px",
    labelFontWeight: 700,
    posFontSize: "11px",
    posColor: "rgba(255, 255, 255, 0.55)",

    nextHeight: 34,
    nextPaddingX: "14px",
    nextRadius: "999px",
    nextFontSize: "12px",
    nextIconSize: 12,
  },

  /** Right-hand panel: content column + vertical tab rail. */
  panel: {
    width: 340,
    background: palette.white,
    borderLeft: `1px solid ${palette.borderLight}`,

    headerPadding: "18px 20px 14px",
    headerTitleFontSize: "14px",
    headerTitleFontWeight: 800,
    headerMetaFontSize: "11.5px",
    headerMetaFontWeight: 700,

    listPadding: "16px 20px",
    listGap: "10px",

    cardRadius: "12px",
    cardPadding: "13px 15px",
    cardGap: "10px",
    cardIconSize: 16,
    cardNameFontSize: "12.5px",
    cardNameFontWeight: 700,

    progressGap: "6px",
    progressFontSize: "11px",
    progressHeight: 6,
    progressRadius: "999px",

    reviewOnlyFontSize: "11px",
    reviewOnlyFontWeight: 600,

    footnoteFontSize: "11.5px",
    footnoteLineHeight: 1.6,

    railWidth: 56,
    railPaddingTop: "14px",
    railGap: "8px",
    railButtonSize: 38,
    railButtonRadius: "10px",
    railIconSize: 17,
  },

  /**
   * Modal card shell, traced from the design's Signature / Delegate / Decline
   * dialogs so every dialog in this flow shares one frame.
   */
  modal: {
    width: 580,
    radius: "16px",
    shadow: "0 30px 80px rgba(0, 0, 0, 0.4)",
    overlay: "rgba(33, 21, 71, 0.55)",
    accentBarHeight: "4px",

    headerPadding: "24px 30px 0",
    headerGap: "3px",
    titleFontSize: "18px",
    titleFontWeight: 800,
    subtitleFontSize: "12px",

    closeSize: 32,
    closeRadius: "8px",
    closeIconSize: 16,

    /** The design's `18px 30px 0` plus the bottom gap its sections carry. */
    bodyPadding: "18px 30px 22px",

    footerPadding: "16px 24px",
    footerGap: "10px",
    buttonHeight: 44,
    buttonRadius: "9px",
    buttonFontSize: "13.5px",
    buttonFontWeight: 700,
    secondaryPaddingX: "20px",
    secondaryBorderWidth: "1.5px",
  },

  /** "Adopt your signature" pad — Type / Draw / Upload. */
  signaturePad: {
    /** Segmented control replacing the old underlined tabs. */
    tabsPadding: "20px 30px 0",
    tabsTrackBg: palette.railRest,
    tabsTrackRadius: "10px",
    tabsTrackPadding: "4px",
    tabsGap: "4px",
    tabHeight: 36,
    tabRadius: "8px",
    tabFontSize: "12.5px",
    tabFontWeight: 700,
    tabActiveShadow: "0 1px 3px rgba(33, 21, 71, 0.1)",

    /** The pad's own body inset — the footer below carries the bottom gap. */
    bodyPadding: "18px 30px 0",
    bodyMinHeight: 262,

    /** Type tab. */
    typeGap: "14px",
    inputHeight: 46,
    inputPaddingX: "16px",
    inputRadius: "10px",
    inputBorderWidth: "1.5px",
    inputFontSize: "14px",
    inputFontWeight: 600,
    counterFontSize: "11.5px",
    styleGridGap: "9px",
    styleRadius: "9px",
    stylePadding: "10px 8px",
    styleMinHeight: 48,
    styleBorderWidth: "1.5px",
    /**
     * The design's tile sizes are set for a ~7-character name; anything longer
     * ellipsises. Previews shrink proportionally past this length, down to
     * `styleFitMinScale`, so a full name still reads in the tile.
     */
    styleFitReferenceLength: 7,
    styleFitMinScale: 0.55,

    /** Draw tab. */
    canvasRadius: "12px",
    canvasBorderWidth: "1.5px",
    canvasHeight: 220,
    /** Backing-store resolution; the element is stretched to 100% width. */
    canvasBufferWidth: 1160,
    canvasBufferHeight: 440,
    canvasStrokeWidth: 5,
    canvasHintFontSize: "13px",

    /** Upload tab. */
    dropzoneRadius: "12px",
    dropzonePadding: "52px 30px",
    dropzoneBorderWidth: "1.5px",
    dropzoneGap: "10px",
    dropzoneIconSize: 38,
    dropzoneTitleFontSize: "15px",
    dropzoneTitleFontWeight: 600,
    dropzoneHintFontSize: "11.5px",
    previewRadius: "12px",
    previewPadding: "26px",
    previewBorderWidth: "2px",
    previewMaxHeight: 200,
    fileNameFontSize: "12.5px",

    /** Small ghost button — "Clear" / "Remove". */
    ghostHeight: 32,
    ghostPaddingX: "14px",
    ghostRadius: "8px",
    ghostBorderWidth: "1.5px",
    ghostFontSize: "12px",
    ghostGap: "7px",
    ghostIconSize: 13,

    /** Footer. */
    footerPadding: "20px 30px 24px",
    footerGap: "13px",
    legalFontSize: "10.5px",
    legalLineHeight: 1.6,
    buttonHeight: 46,
    buttonRadius: "10px",
    cancelPaddingX: "22px",
    cancelFontSize: "13.5px",
    submitFontSize: "14.5px",
  },

  /** "You already have a signature saved" dialog. */
  savedSignature: {
    previewHeight: 200,
    previewRadius: "12px",
    previewPaddingX: "24px",
    previewBg: palette.hover,
    legalFontSize: "11px",
    legalLineHeight: 1.55,
    legalMarginTop: "14px",
  },

  scrollbar: {
    size: "6px",
    thumb: palette.border,
    thumbHover: palette.borderStrong,
    radius: "3px",
  },
};

export default signerExperienceTokens;
